import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Form, Input, Select, Button } from 'antd';
import {
  updateKnowledgebaseAction,
  getKnowledgebaseAction,
  listKnowledgebaseAction,
} from '../../../../../store/actions/knowledgebase';
import { knowledgebaseItemSlice } from '../../../../../store/slices/knowledgebase.slice';

const { Option } = Select;
const { TextArea } = Input;

// Constants for select options based on the model definition
const ENCOUNTER_PHASES = ['pre-chart', 'in-visit', 'after-visit', 'dictate'];
const ENCOUNTER_TYPES = [
  'intake',
  'followup',
  'ambient',
  'note',
  'icd-10',
  'cpt',
];

const EditKnowledgeBase = ({
  isOpen,
  setIsOpen,
  knowledgebaseId,
  isfilterOn: _isfilterOn,
  filterValue,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [contentText, setContentText] = useState('');
  const { knowledgebaseItem } = useSelector(
    (state) => state?.knowledgebaseState
  );
  const { specialties } = useSelector((state) => ({
    specialties: state?.specialtyState?.getAllSpecialtiesList,
  }));

  useEffect(() => {
    if (isOpen && knowledgebaseId) {
      dispatch(getKnowledgebaseAction(knowledgebaseId));
    }
  }, [dispatch, isOpen, knowledgebaseId]);

  useEffect(() => {
    if (
      knowledgebaseItem &&
      knowledgebaseItem.knowledge_base_id === knowledgebaseId &&
      isOpen
    ) {
      form.setFieldsValue({
        specialty_id: knowledgebaseItem.specialty_id,
        content: knowledgebaseItem.content,
        encounter_phase: knowledgebaseItem.encounter_phase,
        encounter_type: knowledgebaseItem.encounter_type,
      });

      setContentText(knowledgebaseItem.content);
    }
  }, [knowledgebaseItem, isOpen, knowledgebaseId, form]);

  const handleClose = () => {
    form.resetFields();
    setContentText('');
    dispatch(knowledgebaseItemSlice({}));
    setIsOpen(false);
  };

  const handleSubmit = (values) => {
    const payload = {
      specialty_id: values.specialty_id,
      content: contentText.trim(),
      encounter_phase: values.encounter_phase,
      encounter_type: values.encounter_type,
    };

    dispatch(
      updateKnowledgebaseAction(knowledgebaseId, payload, () => {
        dispatch(knowledgebaseItemSlice({}));
        handleClose();
        dispatch(listKnowledgebaseAction(filterValue));
      })
    );
  };

  return (
    <Modal
      title="Edit Knowledge Base Entry"
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={1000}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item name="specialty_id" label="Specialty">
          <Select
            placeholder="Select specialty"
            allowClear
            options={specialties?.map((specialty) => ({
              value: specialty.specialty_id,
              label: specialty.specialty_name,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="encounter_phase"
          label="Encounter Phase"
          rules={[
            { required: true, message: 'Please select an encounter phase' },
          ]}
        >
          <Select placeholder="Select encounter phase" allowClear>
            {ENCOUNTER_PHASES.map((phase) => (
              <Option key={phase} value={phase}>
                {phase}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="encounter_type"
          label="Encounter Type"
          rules={[
            { required: true, message: 'Please select an encounter type' },
          ]}
        >
          <Select placeholder="Select encounter type" allowClear>
            {ENCOUNTER_TYPES.map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="content"
          label="Content"
          rules={[{ required: true, message: 'Please enter content' }]}
        >
          <TextArea
            value={contentText}
            onChange={(e) => setContentText(e.target.value)}
            placeholder="Enter the knowledge base content here"
            autoSize={{ minRows: 5, maxRows: 15 }}
            className="w-full"
          />
        </Form.Item>

        <div className="flex justify-end">
          <Button onClick={handleClose} className="mr-2">
            Cancel
          </Button>
          <Button type="primary" htmlType="submit">
            Save Changes
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default EditKnowledgeBase;
